import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_DASHBOARD_PATH } from '../App';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const formRef = useRef(null);
  const userRef = useRef(null);
  const passRef = useRef(null);

  // Password managers fill inputs without firing React's onChange, so poll the
  // DOM briefly after mount and copy anything we find into state. Keeps the
  // controlled inputs and the browser in sync.
  const syncFromDom = useCallback(() => {
    const u = userRef.current?.value || '';
    const p = passRef.current?.value || '';
    setUsername((prev) => (u && u !== prev ? u : prev));
    setPassword((prev) => (p && p !== prev ? p : prev));
  }, []);

  useEffect(() => {
    syncFromDom();
    const timers = [120, 400, 900, 1800, 3000].map((ms) => setTimeout(syncFromDom, ms));
    return () => timers.forEach(clearTimeout);
  }, [syncFromDom]);

  // Resume an existing session from the httpOnly cookie (survives browser
  // restarts) so the owner isn't asked to log in again.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/session`, {
          credentials: 'include',
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_username', data.username);
        navigate(ADMIN_DASHBOARD_PATH, { replace: true });
      } catch { /* no session - show the form */ }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Hide admin pages from search engines + bookmark previews
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = '-';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setError('');

    // Read straight off the DOM: autofilled values can be present in the
    // inputs while React state is still empty.
    const form = formRef.current;
    let u = userRef.current?.value || '';
    let p = passRef.current?.value || '';
    if (form) {
      const fd = new FormData(form);
      u = (fd.get('username') || u || '').toString().trim();
      p = (fd.get('password') || p || '').toString();
    }
    u = (u || username).trim();
    p = p || password;
    setUsername(u);
    setPassword(p);

    if (!u || !p) {
      setError('Enter your username and password.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username: u, password: p }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store token in localStorage
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_username', data.username);
        navigate(ADMIN_DASHBOARD_PATH);
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-white mb-2">
            OUTLAW MEDIA
          </h1>
          <p className="text-red-500 text-sm font-bold">ADMIN ACCESS</p>
        </div>

        {/* Login Form */}
        <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-8 shadow-2xl">
          <form ref={formRef} onSubmit={handleLogin} className="space-y-6" noValidate>
            {error && (
              <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-white font-bold mb-2 text-sm">
                USERNAME
              </label>
              <input
                type="text"
                ref={userRef}
                name="username"
                id="admin-username"
                data-testid="admin-username"
                value={username}
                onInput={(e) => setUsername(e.target.value)}
                onChange={(e) => setUsername(e.target.value)}
                onBlur={(e) => setUsername(e.target.value)}
                className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500 transition-colors"
                placeholder="Enter username"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-white font-bold mb-2 text-sm">
                PASSWORD
              </label>
              <input
                type="password"
                ref={passRef}
                name="password"
                id="admin-password"
                data-testid="admin-password"
                value={password}
                onInput={(e) => setPassword(e.target.value)}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={(e) => setPassword(e.target.value)}
                className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500 transition-colors"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-red-500/50"
            >
              {loading ? 'LOGGING IN...' : 'LOGIN'}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
          >
            ← Back to Homepage
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
